'use client';

import {
  useDynamicContext,
  useDynamicModals,
} from '@dynamic-labs/sdk-react-core';
import { useMemo } from 'react';

import { tw } from '@repo/ui';
import { shortenEthAddress } from '@repo/utils';

import { Metadata } from '@claim/types';

import { useAirdrop } from '../../../../../hooks/use-airdrop';
import { useIsClaimActive } from '../../../../../hooks/use-claim-active';
import { useWalletLinking } from '../../../../../hooks/use-wallet-linking';
import { ClaimButton } from '../../../../button';
import { SolanaWalletDropdown } from './solana-wallet-dropdown';
import {
  getButtonText,
  getDescriptionText,
  getHeaderText,
} from './text-helpers';

interface SubmitProps {
  hasDood: boolean;
}

export const Submit: React.FC<SubmitProps> = ({ hasDood }) => {
  const { setShowLinkNewWalletModal } = useDynamicModals();
  const isClaimActive = useIsClaimActive();
  const { user } = useDynamicContext();

  const {
    selectedAddress,
    setSelectedAddress,
    hasSolWallet,
    handleUpdateMetadata,
    userReady,
    hasEnoughSol,
    isLoadingBalance,
    updatingMetadata,
  } = useWalletLinking();

  // This hook handles the wallet switching and airdrop claim effect.
  useAirdrop(userReady);

  const isDisabled = useMemo(() => {
    const hasRequiredWallet = hasSolWallet;
    const hasRequiredClaimConditions =
      hasDood && selectedAddress && !isLoadingBalance && hasEnoughSol;

    if (!isClaimActive) {
      return !hasRequiredWallet;
    }

    return !hasRequiredWallet || !hasRequiredClaimConditions;
  }, [
    isClaimActive,
    hasSolWallet,
    selectedAddress,
    isLoadingBalance,
    hasEnoughSol,
    hasDood,
  ]);

  const preRegistrationAddress =
    (user?.metadata as Metadata)?.solanaPreregistrationAddress || null;

  return (
    <div
      className={tw(
        'flex flex-col gap-3 text-white md:px-5 md:pb-4',
        'relative md:mt-8',
      )}
    >
      <div className='mt-4 flex flex-col md:mt-0'>
        <p className='text-sm font-semibold tracking-[.48px] md:text-base'>
          {getHeaderText({
            isClaimActive,
          })}
        </p>
        <p className={tw('text-xs font-thin text-white/50 md:text-base')}>
          {getDescriptionText({
            isClaimActive,
          })}
        </p>
      </div>

      {!hasSolWallet ? (
        <div
          onClick={() => setShowLinkNewWalletModal(true)}
          className={tw(
            'rounded-xl bg-white bg-opacity-[.08] p-4 text-center font-semibold',
            'text-sm hover:cursor-pointer md:text-base',
          )}
        >
          + LINK A SOLANA WALLET
        </div>
      ) : (
        <SolanaWalletDropdown
          selectedAddress={selectedAddress}
          setSelectedAddress={setSelectedAddress}
        />
      )}

      {!isClaimActive && preRegistrationAddress ? (
        <p
          className={tw('mt-4 text-[10px] font-thin text-white/50 md:text-xs')}
        >
          Your wallet{' '}
          <span className='font-semibold'>
            {shortenEthAddress(preRegistrationAddress, 6, 6)}
          </span>{' '}
          is currently registered for the airdrop.
        </p>
      ) : null}

      <ClaimButton
        className='p-[1.5rem]'
        disabled={isDisabled}
        isLoading={updatingMetadata}
        onClick={handleUpdateMetadata}
        hasEnoughSol={!isClaimActive || hasEnoughSol}
        hasSolWallet={hasSolWallet}
        text={getButtonText({
          isClaimActive,
          updatingMetadata,
        })}
      />
    </div>
  );
};
